import React, { useEffect, useState } from "react";
import {
  Breadcrumb,
  Col,
  Row,
  Table,
  Typography,
  Popconfirm,
  Button,
  message,
  Empty,
  Tooltip,
} from "antd";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  LoadingOutlined,
  RetweetOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { get_language, translate } from "../../../locale";
import moment from "moment-timezone";
import UserServices from "../../../services/UserServices";
import FullPageLoader from "../../../components/Loaders/FullPageLoader";
import Company from "../../../services/CompanyServices";
import { TableHelper } from "../../../helpers/TableHelper";

const { Title } = Typography;

const antIcon = (
  <LoadingOutlined
    style={{
      fontSize: 24,
    }}
    spin
  />
);

const RequestsPendings = () => {
  const [loading, setLoading] = useState(false);
  const [loadingResend, setLoadingResend] = useState(false);
  const [data, setData] = useState(false);
  const { id } = useParams();
  const { company } = useLocation().state;

  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 25,
      total: 0,
    },
  });
  const [total, setTotal] = useState(0);
  const roles = {
    "eira-super-admin": translate("Super Admin"),
    "eira-company-admin": translate("Admin"),
    "eira-company-coordinator": translate("Coordinador"),
    "eira-professional": translate("Profesional"),
    "eira-patient": translate("Paciente"),
  };

  useEffect(() => {
    let data = {
      page_size: tableParams.pagination.pageSize,
      page_num: tableParams.pagination.current,
    };
    const sorter = TableHelper.sorter(tableParams);
    if (sorter !== null) {
      data = { ...data, order_by: sorter };
    }
    loadData(data);
  }, [JSON.stringify(tableParams)]);

  const loadData = async (data) => {
    setLoading(true);
    try {
      const response = await Company.companyEnrolledUser(id, data);
      if (response.status >= 200 && response.status < 300) {
        setData(response.data.invites);
        setTotal(response.data.total_pages * tableParams.pagination.pageSize);
      }
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const handleResend = async (data) => {
    setLoadingResend(true);
    try {
      const response = await UserServices.resendInvitation({
        type: "email",
        email: data.email,
        subscription: data.subscription.id,
      });

      if (response.status >= 200 && response.status < 300) {
        message.success(
          translate("La {0} se envió exitosamente.", translate("solicitud"))
        );
        loadData();
      } else {
        message.error(
          translate("La {0} no pudo ser enviada.", translate("solicitud")),
          10
        );
      }

      setLoadingResend(false);
    } catch (error) {
      console.log(error);
      message.error(
        translate("La {0} no pudo ser enviada.", translate("solicitud")),
        10
      );
      setLoadingResend(false);
    }
  };

  const handleDelete = async (data) => {
    setLoading(true);
    try {
      const response = await UserServices.deleteInvitation(data.id);
      if (response.status >= 200 && response.status < 300) {
        message.success(
          translate("La {0} se eliminó exitosamente.", translate("invitación"))
        );
        loadData();
      } else {
        message.error(response.data.message);
      }
      setLoading(false);
    } catch (e) {
      console.log(e);
      message.error(e.message);
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      render: (_, record) => (
        <div className="flex flex-row space-x-4 justify-center items-center">
          <Popconfirm
            title={translate("¿Estás seguro de reenviar la invitación?")}
            okText={translate("Si")}
            cancelText={translate("No")}
            onConfirm={() => handleResend(record)}
          >
            <Tooltip title={translate("Reenviar invitación")}>
              <Button
                type="text"
                icon={<RetweetOutlined />}
                size="small"
                className="border-0"
              />
            </Tooltip>
          </Popconfirm>

          <Popconfirm
            title={translate("¿Estás seguro de eliminar el registro?")}
            okText={translate("Si")}
            cancelText={translate("No")}
            onConfirm={() => handleDelete(record)}
          >
            <Tooltip title={translate("Borrar invitación")}>
              <Button
                type="text"
                icon={<DeleteOutlined />}
                size="small"
                className="border-0"
              />
            </Tooltip>
          </Popconfirm>
        </div>
      ),
      width: 160,
    },
    {
      title: "Suscripción",
      dataIndex: "subscription",
      key: "subscription",
      render: (_, record) => record.subscription.name,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Rol del invitado",
      dataIndex: "rol",
      key: "rol",
      render: (_, record) => roles[record.rol],
    },
    {
      title: "Estado",
      dataIndex: "status",
      key: "status",
      render: (_, record) => {
        const diff = moment(record.expire_date).diff(moment(), "days");
        if (diff < 1) {
          return (
            <div className="text-custom_darkred">{translate("Expirado")}</div>
          );
        }

        return (
          <>
            <div className="text-custom_darkgreen leading-none">
              {translate("Pendiente")}
            </div>
            <div className="text-xs text-custom_darkgray leading-none">
              {translate("(expira en {0} días)", diff)}
            </div>
          </>
        );
      },
    },
    {
      title: "Invitado por",
      dataIndex: "full_name",
      key: "full_name",
      render: (_, record) => record.emisor.full_name,
    },
  ];

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      filters,
      sorter,
    });

    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setData([]);
    }
  };
  return (
    <>
      <div className="flex flex-col min-h-full w-screen max-w-7xl pb-16 m-10">
        <Row>
          <Col className="gutter-row" span="12">
            <Breadcrumb>
              <Breadcrumb.Item>
                <Link to="/admin">{translate("Inicio")}</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <Link to="/admin/companies">{translate("Empresas")}</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <Link to={`/admin/companies/${company.id}`}>
                  {company !== null && company.name}
                </Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                {translate("Invitaciones pendientes")}
              </Breadcrumb.Item>
            </Breadcrumb>
          </Col>
          <Col span="24" className="my-4">
            <Title level={2} className="text-center">
              {translate("Invitaciones pendientes")}
            </Title>
          </Col>

          <Col span="24">
            <Table
              rowKey={(i) => i.id}
              dataSource={data}
              columns={columns}
              loading={
                loading && { indicator: antIcon, className: "text-custom_blue" }
              }
              locale={{
                emptyText: <Empty description={translate("No hay datos")} />,
                sortTitle: translate("Ordenar"),
                triggerDesc: translate("Ordenar (descendente)"),
                triggerAsc: translate("Ordenar (ascendente)"),
                cancelSort: translate("Cancelar orden"),
              }}
              pagination={{ ...tableParams.pagination, total: total }}
              onChange={handleTableChange}
            />
          </Col>
        </Row>
      </div>
      {loadingResend && <FullPageLoader />}
    </>
  );
};

export default RequestsPendings;
